import React, { useState, useEffect } from 'react';
import { ref, onValue, getDatabase } from 'firebase/database';
import { useCurrentUid } from "../hooks/useCurrentUid";
import { Truck, Calendar, MapPin, AlertCircle, FileText, ArrowRight } from 'lucide-react';
import "./TransportGoods.css";
import { MdInfo } from 'react-icons/md';

const TransportGoods = () => {
    const userId = useCurrentUid();
    const [dispatches, setDispatches] = useState([]);
    const [transporterDetails, setTransporterDetails] = useState({});
    const [loading, setLoading] = useState(true);
    const [selectedBillDispatch, setSelectedBillDispatch] = useState(null);
    const [openDetails, setOpenDetails] = useState({});
    const [openBank, setOpenBank] = useState({});

   useEffect(() => {
    if (!userId) {
        setLoading(false);
        return;
    }

    const db = getDatabase();
    const dispatchRef = ref(db, `Transport-dispatches/${userId}`);

    let transporterUnsubs = [];

    const unsubscribe = onValue(dispatchRef, (snapshot) => {
        // 🧹 clear old listeners first
        transporterUnsubs.forEach(unsub => unsub());
        transporterUnsubs = [];

        if (snapshot.exists()) {
            const list = Object.entries(snapshot.val()).map(([id, data]) => ({
                id,
                ...data,
            }));

            list.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
            setDispatches(list);

            const transporterIds = [...new Set(
                list.map(d => d.transporterId).filter(Boolean)
            )];

            transporterIds.forEach(transporterId => {
                const transporterRef = ref(db, `transporters/${transporterId}`);

                const unsub = onValue(transporterRef, (snapshot) => {
                    if (snapshot.exists()) {
                        setTransporterDetails(prev => ({
                            ...prev,
                            [transporterId]: snapshot.val()
                        }));
                    }
                });

                transporterUnsubs.push(unsub);
            });

        } else {
            setDispatches([]);
        }

        setLoading(false);
    });

    return () => {
        unsubscribe();
        transporterUnsubs.forEach(unsub => unsub());
    };

}, [userId]);

    const downloadFile = (url, fileName) => {
        if (url) {
            const link = document.createElement('a');
            link.href = url;
            link.target = '_blank';
            link.download = fileName || 'document';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        }
    };

    if (loading) {
        return (
            <div className="tg-loading-container">
                <div className="tg-loader"></div>
            </div>
        );
    }

    const transinfo = () => {
        alert(
            "Transport Goods Information\n\n" +
            "• This window shows all your transport dispatch requests.\n" +
            "• View complete transporter details including bank information, contacts, and GST.\n" +
            "• See the route (From → To), dispatch & delivery dates, and any reported issues.\n" +
            "• Download all attached documents (Bill, Statement, Credit Note) directly from here.\n\n" +
            "All data is updated in real-time from the admin panel.\n" +
            "Contact support if you have any questions."
        );
    }

    return (
        <div className="tg-transport-goods-page">
            <div className="tg-page-header">

                <div>
                    <h1 className="tg-main-title">Transport
                        <Truck size={25} className="tg-header-icon" />
                        <MdInfo className='icon-i' onClick={transinfo} />

                    </h1>
                    <p className="tg-subtitle">Your Complete Transport Dispatch History</p>
                </div>
            </div>

            <div className="tg-main-container">
                {dispatches.length === 0 ? (
                    <div className="tg-empty-state">
                        <Truck size={85} className="tg-empty-icon" />
                        <h2>No Transport Dispatches Yet</h2>

                    </div>
                ) : (
                    <div className="tg-dispatch-list">
                        {dispatches.map((dispatch, index) => (
                            <div key={dispatch.id} className="tg-dispatch-item">
                                <div className="tg-item-header">
                                    <div className="tg-item-date">
                                        {new Date(dispatch.timestamp).toLocaleDateString('en-IN', {
                                            day: 'numeric',
                                            month: 'long',
                                            year: 'numeric'
                                        })}
                                    </div>
                                    {/* BUTTON */}
                                    <button
                                        className="tg-view-btn"
                                        onClick={() =>
                                            setOpenDetails(prev => ({
                                                ...prev,
                                                [dispatch.id]: !prev[dispatch.id]
                                            }))
                                        }
                                    >
                                        {openDetails[dispatch.id] ? "Hide Details ▲" : "View Details ▼"}
                                    </button>
                                </div>

                                <div className="tg-transporter-row">
                                    <strong>Transporter:</strong>
                                    <span className="tg-transporter-name">
                                        {dispatch.transporterName || 'N/A'}
                                    </span>

                                </div>

                                {/* Transporter Details Card - Always Visible */}
                                {transporterDetails[dispatch.transporterId] && (
                                    <div className="tg-transporter-simple">

                                        {/* BASIC INFO */}

                                        <p>
                                            <strong>Name:</strong> {transporterDetails[dispatch.transporterId].contacts
                                                ? Object.values(transporterDetails[dispatch.transporterId].contacts)[0]?.name || "N/A"
                                                : "N/A"}
                                        </p>

                                        <p>
                                            <strong>Phone:</strong>{" "}
                                            {transporterDetails[dispatch.transporterId].contacts
                                                ? Object.values(transporterDetails[dispatch.transporterId].contacts)[0]?.phone || "N/A"
                                                : "N/A"}
                                        </p>



                                        {/* HIDDEN DETAILS */}
                                        {openDetails[dispatch.id] && (
                                            <div className="tg-hidden-details">

                                                {/* GST + STATE */}
                                                <div className="tg-detail-section">
                                                    <h5>General Info</h5>

                                                    <div className="tg-detail-row">
                                                        <span>GST</span>
                                                        <span>{transporterDetails[dispatch.transporterId].gstNumber || 'N/A'}</span>
                                                    </div>

                                                    <div className="tg-detail-row">
                                                        <span>State</span>
                                                        <span>{transporterDetails[dispatch.transporterId].state || 'N/A'}</span>
                                                    </div>
                                                </div>

                                                {/* CONTACTS */}
                                                {transporterDetails[dispatch.transporterId].contacts && (
                                                    <div className="tg-detail-section">
                                                        <h5>Contacts</h5>

                                                        {Object.values(transporterDetails[dispatch.transporterId].contacts).map((c, i) => (
                                                            <div className="tg-detail-row" key={i}>
                                                                <span>{c.name || "Contact"}</span>
                                                                <span className="tg-phone">{c.phone || "N/A"}</span>
                                                            </div>
                                                        ))}
                                                    </div>
                                                )}

                                                {/* BANK */}
                                                {transporterDetails[dispatch.transporterId].bankDetails && (
                                                    <div className="tg-detail-section">

                                                        {/* BANK HEADER WITH TOGGLE */}
                                                        <div
                                                            className="tg-toggle-header"
                                                            onClick={() =>
                                                                setOpenBank(prev => ({
                                                                    ...prev,
                                                                    [dispatch.id]: !prev[dispatch.id]
                                                                }))
                                                            }
                                                        >
                                                            <h5>Bank Details  <span>
                                                                {openBank[dispatch.id] ? "▲" : "▼"}
                                                            </span></h5>

                                                        </div>

                                                        {/* BANK CONTENT */}
                                                        {openBank[dispatch.id] && (
                                                            <div className="tg-bank-content">

                                                                <div className="tg-detail-row">
                                                                    <span>Account Holder</span>
                                                                    <span>{transporterDetails[dispatch.transporterId].bankDetails.accountHolderName}</span>
                                                                </div>

                                                                <div className="tg-detail-row">
                                                                    <span>Account No</span>
                                                                    <span>{transporterDetails[dispatch.transporterId].bankDetails.accountNumber}</span>
                                                                </div>

                                                                <div className="tg-detail-row">
                                                                    <span>Bank</span>
                                                                    <span>{transporterDetails[dispatch.transporterId].bankDetails.bankName}</span>
                                                                </div>

                                                                <div className="tg-detail-row">
                                                                    <span>Branch</span>
                                                                    <span>{transporterDetails[dispatch.transporterId].bankDetails.branchName}</span>
                                                                </div>

                                                                <div className="tg-detail-row">
                                                                    <span>IFSC</span>
                                                                    <span>{transporterDetails[dispatch.transporterId].bankDetails.ifscCode}</span>
                                                                </div>

                                                            </div>
                                                        )}
                                                    </div>
                                                )}

                                            </div>
                                        )}

                                    </div>
                                )}
                                <div className="tg-dispatch-section">

                                    <h4 className="tg-section-title">Dispatch Details</h4>

                                    <div className="tg-route-pill">
                                        <strong >From :</strong> {" "}
                                        <span className="tg-pill from">
                                             {dispatch.routeFrom || "N/A"}
                                        </span>

                                        <span className="tg-arrow"><ArrowRight/></span>
                                         <strong>To :</strong> {" "}
                                        <span className="tg-pill to">
                                            {dispatch.routeTo || "N/A"}
                                        </span>
                                    </div>

                                </div>

                                {dispatch.hasIssue && (
                                    <div className="tg-issue-row">
                                        <AlertCircle size={20} />
                                        <span>Issue Reported: </span>
                                        {dispatch.issueNote && <p className="tg-issue-note">{dispatch.issueNote}</p>}
                                    </div>
                                )}

                                {dispatch.files && Object.keys(dispatch.files).length > 0 && (
                                    <div className="tg-documents-row">
                                        <strong>Documents:</strong>
                                        <div className="tg-files">
                                            {Object.entries(dispatch.files).map(([key, url]) => (
                                                <button
                                                    key={key}
                                                    className="tg-file-btn"
                                                    onClick={() => downloadFile(url, `${key}-${dispatch.id}`)}
                                                >
                                                    <FileText size={18} />
                                                    {key.charAt(0).toUpperCase() + key.slice(1)}
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

export default TransportGoods;
